import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Modal from '../../components/Modal';

const TimeAnalyticsPage = () => {
  const { user } = useAuth();
  const [timeAnalytics, setTimeAnalytics] = useState(null);
  const [dailyReports, setDailyReports] = useState([]);
  const [batches, setBatches] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingBatch, setLoadingBatch] = useState(false);
  const [loadingDoctor, setLoadingDoctor] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    date_from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    date_to: moment().format('YYYY-MM-DD')
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [batchAnalytics, setBatchAnalytics] = useState(null);
  const [doctorOverall, setDoctorOverall] = useState(null);
  const [doctorFilter, setDoctorFilter] = useState('active'); // 'all', 'active', 'inactive'

  useEffect(() => {
    loadTimeAnalytics();
    loadBatches();
    loadDoctors();
    loadDailyReports();
  }, []);

  // Reload doctors when doctorFilter changes
  useEffect(() => {
    loadDoctors();
  }, [doctorFilter]);

  const loadTimeAnalytics = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(dateRange);
      const response = await api.get(`/reports/time-analytics?${params}`);
      if (response.data.success) {
        setTimeAnalytics(response.data.analytics);
      }
    } catch (err) {
      console.error('Failed to load time analytics:', err);
      setError('Failed to load time analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadBatches = async () => {
    try {
      console.log('🔍 DEBUG: Starting loadBatches...');
      console.log('🔍 DEBUG: Current user:', user);
      console.log('🔍 DEBUG: User role:', user?.role);

      const response = await api.get('/batches');
      console.log('🔍 DEBUG: Batches API response:', response);
      console.log('🔍 DEBUG: Response status:', response.status);
      console.log('🔍 DEBUG: Response data type:', typeof response.data);
      console.log('🔍 DEBUG: Response data keys:', Object.keys(response.data || {}));
      console.log('🔍 DEBUG: Response data:', JSON.stringify(response.data, null, 2));

      let batchesArray = [];

      if (response.data && Array.isArray(response.data)) {
        // Response is directly an array
        batchesArray = response.data;
        console.log('✅ DEBUG: Response is direct array, loaded batches:', batchesArray.length);
      } else if (response.data && response.data.batches && Array.isArray(response.data.batches)) {
        // Response has batches key
        batchesArray = response.data.batches;
        console.log('✅ DEBUG: Response has batches key, loaded batches:', batchesArray.length);
      } else if (response.data && typeof response.data === 'object') {
        // Try to find any array in the response
        const arrayKeys = Object.keys(response.data).filter(key => Array.isArray(response.data[key]));
        if (arrayKeys.length > 0) {
          batchesArray = response.data[arrayKeys[0]];
          console.log(`✅ DEBUG: Found array in key '${arrayKeys[0]}', loaded batches:`, batchesArray.length);
        } else {
          console.log('❌ DEBUG: No array found in response object');
        }
      } else {
        console.log('❌ DEBUG: Unexpected response format');
      }

      setBatches(batchesArray);
      console.log('✅ DEBUG: Final batches set:', batchesArray.length);

      if (batchesArray.length === 0) {
        console.log('⚠️ DEBUG: Empty batches array - this might be the issue');
        console.log('⚠️ DEBUG: User role:', user?.role);
        console.log('⚠️ DEBUG: Check if user has permission to view batches');
      }

    } catch (err) {
      console.error('❌ DEBUG: Failed to load batches:', err);
      console.error('❌ DEBUG: Error status:', err.response?.status);
      console.error('❌ DEBUG: Error data:', err.response?.data);
      console.error('❌ DEBUG: Error message:', err.message);
      setBatches([]);
    }
  };

  const loadDoctors = async () => {
    try {
      const response = await api.get('/users?role=doctor');
      console.log('Doctors API response:', response);
      let doctorsList = [];
      
      if (response.data && response.data.users) {
        doctorsList = response.data.users || [];
      } else if (response.data && Array.isArray(response.data)) {
        doctorsList = response.data || [];
      }
      
      // Apply doctor status filter
      if (doctorFilter === 'active') {
        doctorsList = doctorsList.filter(d => d.is_active !== false && !d.email?.startsWith('deleted_') && !d.email?.startsWith('delete_delete_'));
      } else if (doctorFilter === 'inactive') {
        doctorsList = doctorsList.filter(d => d.is_active === false || d.email?.startsWith('deleted_') || d.email?.startsWith('delete_delete_'));
      }
      
      setDoctors(doctorsList);
      console.log('Loaded doctors:', doctorsList);
    } catch (err) {
      console.error('Failed to load doctors:', err);
      setDoctors([]);
    }
  };

  const loadDailyReports = async () => {
    try {
      setLoadingDaily(true);
      const params = new URLSearchParams(dateRange);
      const response = await api.get(`/reports/time-analytics/daily?${params}`);
      if (response.data.success) {
        setDailyReports(response.data.daily_reports || []);
      }
    } catch (err) {
      console.error('Failed to load daily reports:', err);
    } finally {
      setLoadingDaily(false);
    }
  };

  const loadBatchAnalytics = async (batchId) => {
    try {
      setLoadingBatch(true);
      const response = await api.get(`/reports/time-analytics/batch/${batchId}`);
      if (response.data.success) {
        setBatchAnalytics(response.data);
      }
    } catch (err) {
      console.error('Failed to load batch analytics:', err);
    } finally {
      setLoadingBatch(false);
    }
  };

  const loadDoctorOverall = async (doctorId) => {
    try {
      setLoadingDoctor(true);
      console.log('Loading doctor overall for ID:', doctorId);
      const response = await api.get(`/reports/time-analytics/doctor-overall?doctor_id=${doctorId}`);
      console.log('Doctor overall API response:', response);
      if (response.data.success) {
        setDoctorOverall(response.data);
        console.log('Doctor overall data set:', response.data);
      } else {
        console.error('Doctor overall API did not return success:', response.data);
      }
    } catch (err) {
      console.error('Failed to load doctor overall report:', err);
    } finally {
      setLoadingDoctor(false);
    }
  };

  const handleDateRangeChange = () => {
    loadTimeAnalytics();
    loadDailyReports();
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0s';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
    return `${remainingSeconds}s`;
  };

  const formatPercentage = (value) => {
    return `${Math.round(value)}%`;
  };

  const renderOverviewTab = () => {
    if (!timeAnalytics) return <div>Loading...</div>;

    return (
      <div className="row">
        <div className="col-12 mb-4">
          <div className="row">
            <div className="col-md-3 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-primary mb-2">
                    <i className="fas fa-clock fa-2x"></i>
                  </div>
                  <h5 className="fw-bold text-primary">{formatTime(timeAnalytics.total_time_seconds)}</h5>
                  <p className="text-muted mb-0 small">Total Time Spent</p>
                </div>
              </div>
            </div>
            
            <div className="col-md-3 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-success mb-2">
                    <i className="fas fa-play-circle fa-2x"></i>
                  </div>
                  <h5 className="fw-bold text-success">{timeAnalytics.total_sessions}</h5>
                  <p className="text-muted mb-0 small">Total Sessions</p>
                </div>
              </div>
            </div>
            
            <div className="col-md-3 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-info mb-2">
                    <i className="fas fa-question-circle fa-2x"></i>
                  </div>
                  <h5 className="fw-bold text-info">{timeAnalytics.total_questions_answered}</h5>
                  <p className="text-muted mb-0 small">Questions Answered</p>
                </div>
              </div>
            </div>
            
            <div className="col-md-3 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-warning mb-2">
                    <i className="fas fa-stopwatch fa-2x"></i>
                  </div>
                  <h5 className="fw-bold text-warning">{formatTime(Math.round(timeAnalytics.average_time_per_question))}</h5>
                  <p className="text-muted mb-0 small">Avg Time per Question</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light">
              <h6 className="mb-0">Time by Batch</h6>
            </div>
            <div className="card-body">
              {timeAnalytics.by_batch && timeAnalytics.by_batch.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Batch</th>
                        <th>Time</th>
                        <th>Sessions</th>
                        <th>Questions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeAnalytics.by_batch.map((batch, index) => (
                        <tr key={index}>
                          <td>{batch.name}</td>
                          <td>{batch.formatted_time}</td>
                          <td>{batch.total_sessions}</td>
                          <td>{batch.total_questions_answered}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted text-center">No batch data available</p>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-6 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light">
              <h6 className="mb-0">Time by Doctor</h6>
            </div>
            <div className="card-body">
              {timeAnalytics.by_doctor && timeAnalytics.by_doctor.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Doctor</th>
                        <th>Time</th>
                        <th>Sessions</th>
                        <th>Questions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeAnalytics.by_doctor.map((doctor, index) => (
                        <tr key={index}>
                          <td>{doctor.name}</td>
                          <td>{doctor.formatted_time}</td>
                          <td>{doctor.total_sessions}</td>
                          <td>{doctor.total_questions_answered}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted text-center">No doctor data available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDailyTab = () => {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-light">
          <h6 className="mb-0">Daily Time Reports</h6>
        </div>
        <div className="card-body">
          {loadingDaily ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : dailyReports.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Total Time</th>
                    <th>Sessions</th>
                    <th>Questions</th>
                    <th>Active Doctors</th>
                    <th>Active Batches</th>
                    <th>Active Protocols</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyReports.map((report, index) => (
                    <tr key={index}>
                      <td>{moment(report.date).format('MMM DD, YYYY')}</td>
                      <td>{report.formatted_time}</td>
                      <td>{report.total_sessions}</td>
                      <td>{report.total_questions_answered}</td>
                      <td>{report.doctors_active}</td>
                      <td>{report.batches_active}</td>
                      <td>{report.protocols_active}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted text-center">No daily reports available</p>
          )}
        </div>
      </div>
    );
  };

  const renderBatchTab = () => {
    return (
      <div className="row">
        <div className="col-md-4 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light">
              <h6 className="mb-0">Select Batch</h6>
            </div>
            <div className="card-body">
              {loadingBatch ? (
                <div className="text-center">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  <select 
                    className="form-select" 
                    value={selectedBatch?.id || ''} 
                    onChange={(e) => {
                      const batch = batches.find(b => b.id === parseInt(e.target.value));
                      setSelectedBatch(batch);
                      if (batch) loadBatchAnalytics(batch.id);
                    }}
                  >
                    <option value="">Choose a batch...</option>
                    {batches.map(batch => (
                      <option key={batch.id} value={batch.id}>{batch.name}</option>
                    ))}
                  </select>
                  {batches.length === 0 && (
                    <div className="mt-2 text-muted small">
                      No batches available. Please check if batches exist in the system.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {batchAnalytics && (
          <div className="col-md-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <h6 className="mb-0">Batch Analytics: {batchAnalytics.batch.name}</h6>
              </div>
              <div className="card-body">
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="text-center">
                      <h5 className="text-primary">{formatTime(batchAnalytics.batch_stats.total_time_seconds)}</h5>
                      <small className="text-dark fw-bold">Total Time</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center">
                      <h5 className="text-success">{batchAnalytics.batch_stats.total_sessions}</h5>
                      <small className="text-dark fw-bold">Sessions</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center">
                      <h5 className="text-info">{batchAnalytics.batch_stats.doctors_involved}</h5>
                      <small className="text-dark fw-bold">Doctors</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center">
                      <h5 className="text-warning">{batchAnalytics.batch_stats.total_questions_answered}</h5>
                      <small className="text-dark fw-bold">Questions</small>
                    </div>
                  </div>
                </div>

                {batchAnalytics.doctor_stats && batchAnalytics.doctor_stats.length > 0 && (
                  <div className="mb-4">
                    <h6>Doctor Performance</h6>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Doctor</th>
                            <th>Time</th>
                            <th>Sessions</th>
                            <th>Questions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchAnalytics.doctor_stats.map((doctor, index) => (
                            <tr key={index}>
                              <td>{doctor.doctor_name}</td>
                              <td>{doctor.formatted_time}</td>
                              <td>{doctor.total_sessions}</td>
                              <td>{doctor.total_questions_answered}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {batchAnalytics.protocol_stats && batchAnalytics.protocol_stats.length > 0 && (
                  <div>
                    <h6>Protocol Performance</h6>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Protocol</th>
                            <th>Time</th>
                            <th>Sessions</th>
                            <th>Questions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchAnalytics.protocol_stats.map((protocol, index) => (
                            <tr key={index}>
                              <td>{protocol.protocol_name}</td>
                              <td>{protocol.formatted_time}</td>
                              <td>{protocol.total_sessions}</td>
                              <td>{protocol.total_questions_answered}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDoctorTab = () => {
    return (
      <div className="row">
        <div className="col-md-4 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light">
              <h6 className="mb-0">Select Doctor</h6>
            </div>
            <div className="card-body">
              {loadingDoctor ? (
                <div className="text-center">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Doctor Status Filter Dropdown */}
                  <div className="mb-3">
                    <label className="form-label">Doctor Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={doctorFilter}
                      onChange={(e) => setDoctorFilter(e.target.value)}
                    >
                      <option value="all">All Doctors</option>
                      <option value="active">Active Only</option>
                      <option value="inactive">Inactive Only</option>
                    </select>
                  </div>
                  <select 
                    className="form-select" 
                    value={selectedDoctor?.id || ''} 
                    onChange={(e) => {
                      const doctor = doctors.find(d => d.id === parseInt(e.target.value));
                      setSelectedDoctor(doctor);
                      if (doctor) loadDoctorOverall(doctor.id);
                    }}
                  >
                    <option value="">Choose a doctor...</option>
                    {doctors.map(doctor => (
                      <option key={doctor.id} value={doctor.id}>{doctor.name}</option>
                    ))}
                  </select>
                  {doctors.length === 0 && (
                    <div className="mt-2 text-muted small">
                      No doctors available. Please check if doctors exist in the system.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {selectedDoctor && (
          <div className="col-md-8">
            {loadingDoctor ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading doctor analytics...</span>
                </div>
                <p className="mt-2 text-muted">Loading analytics for {selectedDoctor.name}...</p>
              </div>
            ) : doctorOverall ? (
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light">
                  <h6 className="mb-0">Doctor Overall Report: {doctorOverall.doctor?.name || selectedDoctor.name}</h6>
                </div>
                <div className="card-body">
                  <div className="row mb-4">
                    <div className="col-md-3">
                      <div className="text-center">
                        <h5 className="text-primary">{doctorOverall.overall_stats.formatted_time.total_time}</h5>
                        <small className="text-dark fw-bold">Total Time</small>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h5 className="text-success">{doctorOverall.overall_stats.total_sessions}</h5>
                        <small className="text-dark fw-bold">Sessions</small>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h5 className="text-info">{doctorOverall.overall_stats.total_questions_answered}</h5>
                        <small className="text-dark fw-bold">Questions</small>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h5 className="text-warning">{doctorOverall.overall_stats.formatted_time.average_question_time}</h5>
                        <small className="text-dark fw-bold">Avg per Question</small>
                      </div>
                    </div>
                  </div>

                  {doctorOverall.per_question_stats && doctorOverall.per_question_stats.length > 0 && (
                    <div className="mb-4">
                      <h6>Question Performance Analysis</h6>
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Question</th>
                              <th>Category</th>
                              <th>Avg Time</th>
                              <th>Times Answered</th>
                            </tr>
                          </thead>
                          <tbody>
                            {doctorOverall.per_question_stats.map((question, index) => (
                              <tr key={index}>
                                <td>{question.question_text}</td>
                                <td><span className="badge bg-light text-muted">{question.question_category}</span></td>
                                <td>{question.formatted_time}</td>
                                <td>{question.times_answered}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {doctorOverall.per_case_stats && doctorOverall.per_case_stats.length > 0 && (
                    <div className="mb-4">
                      <h6>Case Performance Analysis</h6>
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Case ID</th>
                              <th>Customer</th>
                              <th>Total Time</th>
                              <th>Sessions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {doctorOverall.per_case_stats.map((case_item, index) => (
                              <tr key={index}>
                                <td>{case_item.case_id}</td>
                                <td>{case_item.customer_id}</td>
                                <td>{case_item.formatted_time}</td>
                                <td>{case_item.sessions_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {doctorOverall.per_protocol_stats && doctorOverall.per_protocol_stats.length > 0 && (
                    <div>
                      <h6>Protocol Performance Analysis</h6>
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Protocol</th>
                              <th>Total Time</th>
                              <th>Sessions</th>
                              <th>Avg per Session</th>
                            </tr>
                          </thead>
                          <tbody>
                            {doctorOverall.per_protocol_stats.map((protocol, index) => (
                              <tr key={index}>
                                <td>{protocol.protocol_name}</td>
                                <td>{protocol.formatted_time}</td>
                                <td>{protocol.sessions_count}</td>
                                <td>{formatTime(Math.round(protocol.average_time_per_session))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-4">
                  <p className="text-muted">No analytics data available for this doctor.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <ResearchLayout pageTitle="Time Analytics">
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col">
            <div className="card border-0 shadow-sm bg-gradient-info text-white">
              <div className="card-body" style={{backgroundColor: '#7B68EE'}}>
                <h3 className="mb-2">Time Analytics</h3>
                <p className="mb-0 opacity-75">
                  View detailed time tracking reports for doctors, batches, and protocols
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <label className="form-label">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateRange.date_from}
                      onChange={(e) => setDateRange({...dateRange, date_from: e.target.value})}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateRange.date_to}
                      onChange={(e) => setDateRange({...dateRange, date_to: e.target.value})}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">&nbsp;</label>
                    <button 
                      className="btn btn-primary d-block" 
                      onClick={handleDateRangeChange}
                    >
                      <i className="fas fa-filter me-2"></i>
                      Apply Filter
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-dark">
                <ul className="nav nav-tabs card-header-tabs">
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                      onClick={() => setActiveTab('overview')}
                    >
                      <i className="fas fa-chart-bar me-2"></i>
                      Overview
                    </button>
                  </li>
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${activeTab === 'daily' ? 'active' : ''}`}
                      onClick={() => setActiveTab('daily')}
                    >
                      <i className="fas fa-calendar-day me-2"></i>
                      Daily Reports
                    </button>
                  </li>
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${activeTab === 'batch' ? 'active' : ''}`}
                      onClick={() => setActiveTab('batch')}
                    >
                      <i className="fas fa-layer-group me-2"></i>
                      Batch Reports
                    </button>
                  </li>
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${activeTab === 'doctor' ? 'active' : ''}`}
                      onClick={() => setActiveTab('doctor')}
                    >
                      <i className="fas fa-user-md me-2"></i>
                      Doctor Reports
                    </button>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                ) : (
                  <>
                    {activeTab === 'overview' && renderOverviewTab()}
                    {activeTab === 'daily' && renderDailyTab()}
                    {activeTab === 'batch' && renderBatchTab()}
                    {activeTab === 'doctor' && renderDoctorTab()}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default TimeAnalyticsPage;